<template>
	<div class="relative">
		<div class="fixed white" style="width: 100%;" id="tool-bar">
			<v-row
				v-if="$route.name === 'final-accounts'"
				style="width: 100%"
				align="center"
				justify="start"
				class="ma-0"
			>
				<v-col
					sm="auto"
					class="d-flex align-start"
				>
					<slot name="breadcrumbs"></slot>
				</v-col>
				<v-spacer></v-spacer>
				<v-col
					md="auto"
					sm="auto"
					cols="12"
				>
					<!-- export -->
					<v-menu offset-y>
						<template v-slot:activator="{ on }">
							<v-btn dark color="printingColor" class="px-2 mb-md-0 mb-1 me-auto" v-on="on">
								<span class="d-flex align-center">
									<v-icon color="white">mdi-export-variant</v-icon>
									<span style="font-size: 14px">
										{{$t('FinalAccountsPage.export')}}
									</span>
								</span>
							</v-btn>
						</template>

						<bee-list>
							<!-- excel -->
							<bee-list-item
								context="final_account"
								:item="exportItems[0]"
								dense
								@click="exportAs('excel')"
							></bee-list-item>

							<!-- pdf -->
							<bee-list-item
								context="final_account"
								:item="exportItems[1]"
								dense
								@click="exportAs('pdf')"
							></bee-list-item>
						</bee-list>
					</v-menu>

					<!-- lock -->
					<bee-btn
						color="greyDarken2"
						:content="
							file_is_locked
							? $t('FinalAccountsPage.unlock-accounts')
							: $t('FinalAccountsPage.lock-accounts')
						"
						@click="file_is_locked ? unlockFinancialFileDialog = true : lockDialog = true"
						:icon="file_is_locked ? 'mdi-lock-open-variant' : 'mdi-lock'"
						outer-class="mb-md-0 mb-1 ms-2"
						context="financial_file"
						:action-name="actions.EDIT"
						v-if="file_is_locked ? self.is_admin : isUserCanLockFinancialFile"
					></bee-btn>
					
					<!-- rotate -->
					<bee-btn
						color="deletingColor"
						outer-class="ms-2"
						:content="rotateBtnText"
						@click="
							!current_financial_file.rotated_financial_file_id || !self.is_admin
								? rotateDialog = true
								: reRotateConfirm()"
						icon="mdi-rotate-3d-variant"
						:loading="financialFileProgressLoading"
						:disabled="!self.is_admin && (!isAllowedToRotate || financialFileProgressLoading)"
						context="financial_file"
						:action-name="actions.CREATE"
						v-if="self.is_admin || isAccountantWithCreatePermission"
					></bee-btn>
				</v-col>
			</v-row>
		</div>
		<div class="ma-auto absolute" id="main" style="width: 100%; height: auto">
			<v-row class="mx-3 fill-height my-0">
				<v-tabs grow background-color="grey lighten-3" v-model="activeTab">
					<v-tab>{{$t('FinalAccountsPage.account.operations')}}</v-tab>
					<v-tab>{{$t('FinalAccountsPage.account.trade')}}</v-tab>
					<v-tab>{{$t('FinalAccountsPage.account.gains-and-losses')}}</v-tab>
					<v-tab>{{$t('FinalAccountsPage.account.budget')}}</v-tab>
					<v-tabs-items v-model="activeTab">
						<v-tab-item
							v-for="item in [0, 1, 2, 3]"
							:key="item"
							style="overflow-y: auto !important; overflow-x: hidden !important; width: 100%"
							class="mb-3"
						>
							<v-card flat>
								<v-card-text class="px-0 pt-3">
									<final-tyep-card
										:active-tab="activeTab"
										:final_account="!isStatisticsView ? final_account : statistics"
										:configs="!isStatisticsView ? configs : { currencies, accounts }"
									></final-tyep-card>
								</v-card-text>
							</v-card>
						</v-tab-item>
					</v-tabs-items>
				</v-tabs>
			</v-row>

			<bee-dialog
				@handler="unlockFinancialFile()"
				@close="unlockFinancialFileDialog = false"
				v-model="unlockFinancialFileDialog"
				:title="$t('FinalAccountsPage.unlock-current-financial-file-accounts')"
				:message="$t('FinalAccountsPage.are-you-sure-you-want-to-unlock-accounts')"
			></bee-dialog>
			
			<!-- rerotate dialog -->
			<bee-dialog
				@handler="reRotateDialog = false; rotateDialog = true"
				@close="reRotateDialog = false"
				v-model="reRotateDialog"
				:title="$t('FinalAccountsPage.rerotate-financial-file')"
				:message="$t('FinalAccountsPage.do-you-want-to-rerotate-financial-file', {financialFile: rotatedFinancialFile.company_name})"
			></bee-dialog>
            
            <!-- footer -->
			<v-bottom-navigation app class="px-3" horizontal background-color="greyLighten3">
				<v-row align="center" justify="center">
					<bee-outlined-header-info :head="$t('FinalAccountsPage.operations-account-balance')">
						<template v-slot:content="{}">
							<bee-advanced-money-label
								empty-state="_"
								v-model="operation_sum_difference"
								:bold="true"
								:with-color="true"
								:currencies="currencies"
							></bee-advanced-money-label>
						</template>
					</bee-outlined-header-info>
					<bee-outlined-header-info
					    :head="$t('FinalAccountsPage.trade-account-balance')"
					    v-if="activeTab != 0"
					>
						<template v-slot:content="{}">
							<bee-advanced-money-label
								empty-state="_"
								v-model="trade_sum_difference"
								:bold="true"
								:with-color="true"
								:currencies="currencies"
							></bee-advanced-money-label>
						</template>
					</bee-outlined-header-info>
					<bee-outlined-header-info
					    :head="$t('FinalAccountsPage.gains-and-losses-account-balance')"
					    v-if="activeTab>=2"
					>
						<template v-slot:content="{}">
							<bee-advanced-money-label
								empty-state="_"
								v-model="gains_and_losses_sum_difference"
								:bold="true"
								:with-color="true"
								:currencies="currencies"
							></bee-advanced-money-label>
						</template>
					</bee-outlined-header-info>
					<bee-outlined-header-info
					    :head="$t('FinalAccountsPage.budget-account-balance')"
					    v-if="activeTab == 3"
					>
						<template v-slot:content="{}">
							<bee-advanced-money-label
								empty-state="_"
								v-model="budget_sum_difference"
								:bold="true"
								:with-color="true"
								:currencies="currencies"
							></bee-advanced-money-label>
						</template>
					</bee-outlined-header-info>
				</v-row>
			</v-bottom-navigation>

			<!-- lock financial file dialog -->
			<lock-financial-file-dialog v-model="lockDialog" @financial-file-locked="fetch(activeTab)"/>

			<!-- rotate dialog -->
			<rotate-financial-file-dialog v-model="rotateDialog"/>
		</div>
	</div>
</template>
<script>
import FinalTyepCard from "./sub-components/FinalTyepCard.vue";
import store from "../../../store/index.js";
import { mapState } from "vuex";
import rules from "../../../validation";
import ls from "../../../settings/localStorageExtension";
import actions from "../../../permissions/actions-names.js";
import LockFinancialFileDialog from './sub-components/LockFinancialFileDialog.vue'
import RotateFinancialFileDialog from './sub-components/RotateFinancialFileDialog'
import { bondType } from '@/helpers/enums';
export default {
	components: {
		FinalTyepCard,
		LockFinancialFileDialog,
		RotateFinancialFileDialog
	},
	data() {
		return {
			actions,
			activeTab: 0,
			lockDialog: false,

			rotateDialog: false,
			
			rotateBtnText: this.$t('FinalAccountsPage.rotate'),

			rotatedFinancialFile: {},
			reRotateDialog: false,

			financialFileProgressLoading: false,
			isAllowedToRotate: true,

			...rules,
			unlockFinancialFileDialog: false,
			loading: false,

			statistics: {},
		};
	},
	meta: {
		title: "final-accounts",
		meta: [{ property: "og:title", content: "final-accounts | Emerald" }]
	},

	watch: {
		activeTab: function(newVal) {
			this.fetch(newVal);
		},

		'$route.query.financialFileId': {
			handler() {
				if (this.isStatisticsView && this.$route.query.financialFileId) {
					this.fetch(this.activeTab);
					this.$store.dispatch('accounts/fetchAll', {
						financialFileId: this.$route.query.financialFileId,
					});
				}
			},
			immediate: true
		}
	},
	computed: {
		...mapState("statements", ["final_account"]),
		...mapState("configs", ["configs"]),
		...mapState("financialFiles", ["current_financial_file"]),
		...mapState("auth", ["self"]),
		...mapState({
			allCurrencies: state => state.currencies.currencies,
			accounts: state => state.accounts.accounts,
		}),
		exportItems() {
			return [
				{
					text: this.$t('FinalAccountsPage.export-as-excel'),
					icon: 'mdi-microsoft-excel',
					context: 'final_account',
					action_name: actions.EXPORT_AS_PDF
				},
				{
					text: this.$t('FinalAccountsPage.print-the-final-accounts'),
					icon: 'mdi-printer',
					context: 'final_account',
					action_name: actions.EXPORT_AS_PDF
				},
			]
		},
		operation_sum_difference() {
			return (!this.isStatisticsView ?  this.final_account : this.statistics)
				?.operation_sum_result?.sum_difference || null;
		},
		trade_sum_difference() {
			return (!this.isStatisticsView ?  this.final_account : this.statistics)
				?.trade_sum_result?.sum_difference || null;
		},
		gains_and_losses_sum_difference() {
			return (!this.isStatisticsView ?  this.final_account : this.statistics)
				?.gains_and_losses_sum_result?.sum_difference || null;
		},
		budget_sum_difference() {
			return (!this.isStatisticsView ?  this.final_account : this.statistics)
				?.budget_sum_result?.sum_difference || null;
		},
		file_is_locked() {
			return this.configs?.financial_file?.is_locked;
		},
		currencies() {
			return !this.isStatisticsView ? this.configs?.currencies : this.allCurrencies;
		},
		isUserCanLockFinancialFile(){
			if (this.self.is_admin) 
				return true

			return this.isAccountantWithCreatePermission
		},
		isAccountantWithCreatePermission() {
			return (ls.getLocalUser().can_create_financial_file && ls.getLocalUser().permission_type == 1)
		},

		isStatisticsView() {
			return this.$route.name.includes('statistics');
		}
	},
	methods: {
		unlockFinancialFile() {
			store.dispatch("financialFiles/unlock", {
				financialFileId: this.configs.financial_file.id,
			}).then(() => {
				store.dispatch("configs/fetchAll");
				store.dispatch("bonds/fetchAll", { bondType: bondType.accounting });
				this.fetch(this.activeTab);
			});
		},

		fetch(activeTab) {
			if (!this.isStatisticsView) {
				switch (activeTab) {
					case 0:
						store.dispatch('statements/fetchOperationsFinalAccounts');
						break;
					case 1:
						store.dispatch('statements/fetchTradeFinalAccount');
						break;
					case 2:
						store.dispatch('statements/fetchGainsAndLossesFinalAccount');
						break;
					case 3:
						store.dispatch('statements/fetchBudgetFinalAccount');
						break;
				}
			} else {
				if (!this.$route.query.financialFileId) return;
				let actionName;
				switch (activeTab) {
					case 0: actionName = 'fetchOperationsFinalAccounts'; break;
					case 1: actionName = 'fetchTradeFinalAccount'; break;
					case 2: actionName = 'fetchGainsAndLossesFinalAccount'; break;
					case 3: actionName = 'fetchBudgetFinalAccount'; break;
				}
				this.$store.dispatch(`statistics/${actionName}`, {
					financialFileId: this.$route.query.financialFileId,
					userId: ls.getLocalUser()?.id
				}).then(data => {
					this.statistics = data;
				});
			}
		},

		exportAs(type) {
			this.$store.dispatch('reports/FinalAccountsReport', {
				type,
				financialFileId: this.configs.financial_file.id,
				userId: ls.getLocalUser().id
			})
		},

		async reRotateConfirm() {
			if (!this.rotatedFinancialFile.company_name) {
				await this.$store.dispatch('financialFiles/fetchFinancialFile', this.current_financial_file.rotated_financial_file_id)
				.then((data) => {
					this.rotatedFinancialFile = data;
				})
			}
			this.reRotateDialog = true;
		}
	},
	created() {
		if (this.self.is_admin || this.isAccountantWithCreatePermission) {
			if (this.current_financial_file.rotated_financial_file_id) {
				this.financialFileProgressLoading = true;
				this.$store.dispatch('financialFiles/fetchRotateProgress', {
					financialFileId: this.configs.financial_file.id,
					rotateFileId: this.current_financial_file.rotated_financial_file_id,
				}).then((data) => {
					this.financialFileProgressLoading = false;
					// if progress step is in addAccessPermissions
					if (data.step === 7) {
						this.isAllowedToRotate = false;
						this.rotateBtnText = this.$t('FinalAccountsPage.re-rotate');
					}
				})
			} else {
				this.isAllowedToRotate = true;
			}
		}

		if (this.isStatisticsView && !this.allCurrencies.length) {
			this.$store.dispatch('currencies/fetchAll');
		}
	},

	beforeDestroy() {
		this.$store.state.accounts.accounts = [];
	},

	mounted() {
		window.observer.observe(document.querySelector("#tool-bar"));
		this.loading = false;
	},
	async beforeRouteEnter(to, from, next) {
		if (to.name.includes('statistics')) {
			next();
		} else {
			const promises = [store.dispatch("statements/fetchOperationsFinalAccounts"), store.dispatch('configs/fetchDefaultFinancialFileAccounts')];
	
			let object = JSON.parse(JSON.stringify(store.state.configs.configs));
			if (Object.keys(object).length == 0)
				promises.push(store.dispatch("configs/fetchAll"));
	
			await Promise.all(promises);
	
			next();
		}
	}
};
</script>
<style scoped>
.my-border {
	border: 1px solid !important;
}
.v-bottom-navigation {
	overflow-y: scroll;
}
::v-deep .theme--dark.v-tabs > .v-tabs-bar .v-tab:not(.v-tab--active),
.theme--dark.v-tabs > .v-tabs-bar .v-tab:not(.v-tab--active) > .v-icon,
.theme--dark.v-tabs > .v-tabs-bar .v-tab:not(.v-tab--active) > .v-btn,
.theme--dark.v-tabs > .v-tabs-bar .v-tab--disabled {
	color: grey;
}
::v-deep .v-tab {
	font-size: 0.99rem;
}
::v-deep .v-tab--active {
	background-color: #f1f1f1 !important;
	color: #346f86;
}
.v-tabs__content {
	background-color: green !important;
	height: 100px;
}
</style>